import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  LuCheck,
  LuClipboardCheck,
  LuClipboardPen,
  LuInfo,
  LuListChecks,
  LuMailCheck,
  LuPackage,
  LuTruck,
  LuWallet,
} from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';
import { Meter } from './VisitUi';

/** Icon and accent of every step. */
export const STEP_META = {
  informations: { icon: LuInfo, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30' },
  todoList: { icon: LuListChecks, gradient: 'tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30' },
  visitForm: { icon: LuClipboardPen, gradient: 'tw-from-cyan-400 tw-to-teal-600 tw-shadow-cyan-500/30' },
  stock: { icon: LuPackage, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30' },
  shipments: { icon: LuTruck, gradient: 'tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30' },
  expenses: { icon: LuWallet, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  visitEvaluation: { icon: LuClipboardCheck, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
  craApproval: { icon: LuMailCheck, gradient: 'tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30' },
};

/** The step's status in a few words: "Tamam", "3 eksik", "2 kayıt", "İsteğe bağlı". */
export const useStepStatusText = () => {
  const { t } = useTranslation();
  return (step) => {
    if (step.state === 'missing') return t('visitModal.status.missing', { count: step.missing.length });
    if (step.state === 'done') return t('visitModal.status.done');
    if (step.state === 'info') return t('visitModal.status.info');
    if (step.records > 0) return t('visitModal.status.records', { count: step.records });
    return t('visitModal.status.optional');
  };
};

const STATUS_TEXT_TONES = {
  missing: 'tw-text-amber-700 dark:tw-text-amber-300',
  done: 'tw-text-emerald-700 dark:tw-text-emerald-300',
  info: 'tw-text-slate-500 dark:tw-text-slate-400',
  optional: 'tw-text-slate-500 dark:tw-text-slate-400',
};

/** Numbered circle: a tick when done, amber while fields are missing. */
export const StepCircle = ({ state, number, active, size = 'md' }) => {
  const sizes = size === 'sm' ? 'tw-h-7 tw-w-7 tw-text-xs' : 'tw-h-9 tw-w-9 tw-text-sm';
  const tones = {
    done: 'tw-bg-emerald-500 tw-text-white tw-shadow-sm tw-shadow-emerald-500/30',
    missing: 'tw-bg-amber-400/20 tw-text-amber-800 tw-ring-2 tw-ring-inset tw-ring-amber-400/70 dark:tw-text-amber-200',
    info: 'tw-bg-slate-100 tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300',
    optional: 'tw-bg-slate-100 tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300',
  };
  const activeRing = active
    ? 'tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-teal-500 dark:tw-outline-teal-400'
    : '';
  return (
    <span className={`tw-relative tw-grid tw-shrink-0 tw-place-items-center tw-rounded-full tw-font-bold tw-tabular-nums ${sizes} ${tones[state]} ${activeRing}`}>
      {state === 'done' ? <LuCheck aria-hidden="true" className={size === 'sm' ? 'tw-h-3.5 tw-w-3.5' : 'tw-h-4 tw-w-4'} strokeWidth={3} /> : number}
    </span>
  );
};

StepCircle.propTypes = {
  state: PropTypes.oneOf(['done', 'missing', 'info', 'optional']).isRequired,
  number: PropTypes.number.isRequired,
  active: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md']),
};

const stepShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  missing: PropTypes.array.isRequired,
  records: PropTypes.number,
});

const progressShape = PropTypes.shape({
  percent: PropTypes.number.isRequired,
  missingCount: PropTypes.number.isRequired,
  ready: PropTypes.bool.isRequired,
});

/** Share of required fields filled in, with what it means in words. */
const ProgressSummary = ({ progress }) => {
  const { t } = useTranslation();
  return (
    <div className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/90 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
      <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
          {t('visitModal.progress.title')}
        </span>
        <span className={`tw-text-lg tw-font-bold tw-tabular-nums ${progress.ready ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-slate-900 dark:tw-text-white'}`}>
          {t('visitModal.progress.percent', { value: progress.percent })}
        </span>
      </div>
      <Meter percent={progress.percent} tone={progress.ready ? 'tw-bg-emerald-500' : 'tw-bg-teal-500'} className="tw-mt-2" />
      <p className={`tw-mt-2 tw-text-xs tw-font-medium tw-leading-snug ${progress.ready ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-text-amber-700 dark:tw-text-amber-300'}`}>
        {progress.ready ? t('visitModal.progress.ready') : t('visitModal.progress.missing', { count: progress.missingCount })}
      </p>
      <p className="tw-mt-1 tw-text-[11px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
        {progress.ready ? t('visitModal.progress.readyHint') : t('visitModal.progress.missingHint')}
      </p>
    </div>
  );
};

ProgressSummary.propTypes = { progress: progressShape.isRequired };

/** Desktop: vertical list of the steps beside the content. */
export const StepRail = ({ steps, activeKey, onSelect, progress }) => {
  const { t } = useTranslation();
  const statusText = useStepStatusText();
  return (
    <div className="tw-sticky tw-top-0 tw-space-y-4 tw-p-4">
      <ProgressSummary progress={progress} />
      <nav aria-label={t('visitModal.progress.stepsLabel')}>
        <ol className="tw-space-y-1">
          {steps.map((step, index) => {
            const active = step.key === activeKey;
            return (
              <li key={step.key} className="tw-relative">
                {index < steps.length - 1 && (
                  <span aria-hidden="true" className="tw-absolute tw-bottom-[-0.375rem] tw-left-[1.625rem] tw-top-[2.875rem] tw-w-px tw-bg-slate-200 dark:tw-bg-white/10" />
                )}
                <button
                  type="button"
                  onClick={() => onSelect(step.key)}
                  aria-current={active ? 'step' : undefined}
                  className={`tw-relative tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-px-2 tw-py-2 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    active
                      ? 'tw-bg-white tw-shadow-sm tw-ring-1 tw-ring-slate-200/80 dark:tw-bg-white/[0.07] dark:tw-ring-white/10'
                      : 'hover:tw-bg-slate-900/[0.04] dark:hover:tw-bg-white/[0.04]'
                  }`}
                >
                  <StepCircle state={step.state} number={index + 1} active={active} />
                  <span className="tw-min-w-0">
                    <span className={`tw-block tw-truncate tw-text-sm ${active ? 'tw-font-semibold tw-text-slate-900 dark:tw-text-white' : 'tw-font-medium tw-text-slate-700 dark:tw-text-slate-200'}`}>
                      {t(`visitModal.steps.${step.key}.label`)}
                    </span>
                    <span className={`tw-block tw-truncate tw-text-xs ${STATUS_TEXT_TONES[step.state]}`}>{statusText(step)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

StepRail.propTypes = {
  steps: PropTypes.arrayOf(stepShape).isRequired,
  activeKey: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  progress: progressShape.isRequired,
};

/** Phones and tablets: sticky strip of step pills over the content. */
export const StepStrip = ({ steps, activeKey, onSelect, progress }) => {
  const { t } = useTranslation();
  const listRef = useRef(null);
  const itemRefs = useRef({});
  const activeIndex = steps.findIndex((step) => step.key === activeKey);

  // Keep the current step's pill in view (horizontally only, so the dialog
  // body doesn't jump).
  useEffect(() => {
    const list = listRef.current;
    const item = itemRefs.current[activeKey];
    if (!list || !item) return;
    list.scrollTo({ left: item.offsetLeft - (list.clientWidth - item.clientWidth) / 2, behavior: 'smooth' });
  }, [activeKey]);

  return (
    <div className="tw-sticky tw-top-0 tw-z-10 tw-border-b tw-border-slate-200/80 tw-bg-white/95 tw-backdrop-blur-xl lg:tw-hidden dark:tw-border-white/10 dark:tw-bg-[#1f232b]/95">
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-pt-3 sm:tw-px-6">
        <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
          {t('visitModal.progress.stepOf', { current: activeIndex + 1, total: steps.length })}
        </span>
        <Meter percent={progress.percent} tone={progress.ready ? 'tw-bg-emerald-500' : 'tw-bg-teal-500'} className="tw-flex-1" />
        <span className={`tw-shrink-0 tw-text-xs tw-font-bold tw-tabular-nums ${progress.ready ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-slate-700 dark:tw-text-slate-200'}`}>
          {t('visitModal.progress.percent', { value: progress.percent })}
        </span>
      </div>
      <nav aria-label={t('visitModal.progress.stepsLabel')}>
        <ol ref={listRef} className="tw-flex tw-gap-1.5 tw-overflow-x-auto tw-px-3 tw-py-2.5 [scrollbar-width:none] sm:tw-px-5 [&::-webkit-scrollbar]:tw-hidden">
          {steps.map((step, index) => {
            const active = step.key === activeKey;
            return (
              <li key={step.key} ref={(node) => { itemRefs.current[step.key] = node; }} className="tw-shrink-0">
                <button
                  type="button"
                  onClick={() => onSelect(step.key)}
                  aria-current={active ? 'step' : undefined}
                  className={`tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-py-1 tw-pl-1 tw-pr-3 tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    active
                      ? 'tw-bg-teal-500/10 tw-ring-1 tw-ring-inset tw-ring-teal-500/30'
                      : 'tw-bg-transparent hover:tw-bg-slate-900/[0.04] dark:hover:tw-bg-white/[0.05]'
                  }`}
                >
                  <StepCircle state={step.state} number={index + 1} size="sm" />
                  <span className={`tw-whitespace-nowrap tw-text-xs tw-font-semibold ${active ? 'tw-text-teal-800 dark:tw-text-teal-200' : step.state === 'missing' ? 'tw-text-amber-800 dark:tw-text-amber-200' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>
                    {t(`visitModal.steps.${step.key}.label`)}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

StepStrip.propTypes = StepRail.propTypes;
